import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, downloadText } from "../lib/api";
import { SECTORS, SECTOR_KEYS } from "../lib/sectors";
import { ContentModal } from "../components/ContentModal";
import { useConfirm } from "../components/useConfirm";
import { Plus, Pencil, Trash2, Download, Check, CalendarDays } from "lucide-react";

const dayLabel = (iso) => new Date(iso).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

export function CalendarView({ allowedSectors }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { confirm, ConfirmUI } = useConfirm();
  const sectors = allowedSectors && allowedSectors.length ? allowedSectors : SECTOR_KEYS;

  const load = () => api.get("/contents").then((r) => setItems(r.data.filter((c) => sectors.includes(c.sector))));
  useEffect(() => { load(); }, []);

  const shown = filter === "all" ? items : items.filter((c) => c.sector === filter);

  // group by date
  const groups = {};
  shown.forEach((c) => { (groups[c.date] = groups[c.date] || []).push(c); });
  const dates = Object.keys(groups).sort();

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setOpen(true); };
  const toggleComplete = async (c) => { await api.put(`/contents/${c.id}`, { completed: !c.completed }); load(); };
  const remove = async (c) => {
    if (!(await confirm({ title: "Eliminare il contenuto?", description: c.title, confirmLabel: "Elimina", variant: "danger" }))) return;
    await api.delete(`/contents/${c.id}`); toast.success("Eliminato"); load();
  };
  const exportMd = () => {
    const md = dates.map((d) => {
      const lines = groups[d].map((c) => `- [${c.completed ? "x" : " "}] **${c.title}** (${SECTORS[c.sector].short} · ${c.type} · ${c.status})${c.body ? `\n  ${c.body.replace(/\n/g, " ")}` : ""}`).join("\n");
      return `## ${dayLabel(d)}\n${lines}`;
    }).join("\n\n");
    downloadText("calendario-contenuti.md", `# Calendario contenuti\n\n${md || "Nessun contenuto."}`);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="calendar-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {sectors.length > 1 && <FilterChip id="all" filter={filter} setFilter={setFilter} label="Tutti" hex="#f1f5f9" />}
          {sectors.map((k) => <FilterChip key={k} id={k} filter={filter} setFilter={setFilter} label={SECTORS[k].short} hex={SECTORS[k].hex} />)}
        </div>
        <div className="flex gap-2">
          <button data-testid="export-calendar-button" onClick={exportMd} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"><Download className="h-4 w-4" /> <span className="hidden sm:inline">Esporta</span></button>
          <button data-testid="new-content-button" onClick={openNew} className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-blue-500 text-white font-semibold hover:opacity-90 transition-opacity"><Plus className="h-4 w-4" /> Nuovo contenuto</button>
        </div>
      </div>

      {dates.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-slate-400">
          <CalendarDays className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Nessun contenuto pianificato. Crea il primo contenuto o salva un'idea dai generatori AI.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {dates.map((d) => (
            <div key={d}>
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 capitalize">{dayLabel(d)}</p>
              <div className="space-y-2">
                {groups[d].map((c) => {
                  const S = SECTORS[c.sector];
                  return (
                    <motion.div key={c.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                      className="glass rounded-xl p-3.5 flex items-center gap-3 group" style={{ borderLeft: `3px solid ${S.hex}` }} data-testid={`content-${c.id}`}>
                      <button data-testid={`complete-content-${c.id}`} onClick={() => toggleComplete(c)}
                        className="h-5 w-5 shrink-0 rounded-md border grid place-items-center transition-colors"
                        style={c.completed ? { background: S.hex, borderColor: S.hex } : { borderColor: "rgba(255,255,255,0.2)" }}>
                        {c.completed && <Check className="h-3 w-3 text-white" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${c.completed ? "line-through text-slate-500" : ""}`}>{c.title}</p>
                        <p className="text-[11px] text-slate-500 capitalize">{S.short} · {c.type} · {c.status}</p>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button data-testid={`edit-content-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-white/10"><Pencil className="h-3.5 w-3.5 text-slate-400" /></button>
                        <button data-testid={`delete-content-${c.id}`} onClick={() => remove(c)} className="p-1.5 rounded-lg hover:bg-rose-500/20"><Trash2 className="h-3.5 w-3.5 text-rose-400" /></button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <ContentModal open={open} onClose={() => setOpen(false)} onSaved={load} editing={editing} allowedSectors={sectors} />
      {ConfirmUI}
    </div>
  );
}

function FilterChip({ id, filter, setFilter, label, hex }) {
  const on = filter === id;
  return (
    <button data-testid={`calendar-filter-${id}`} onClick={() => setFilter(id)}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors"
      style={on ? { background: `${hex}22`, color: hex, borderColor: `${hex}66` } : { color: "#94a3b8", borderColor: "rgba(255,255,255,0.08)" }}>
      <span className="h-2 w-2 rounded-full" style={{ background: hex }} /> {label}
    </button>
  );
}
